// src/pages/register/business/steps/BusinessStepSix/PlanSelection.tsx
import React, { useEffect, useMemo, useState } from 'react';
import { motion } from 'framer-motion';
import {
  Star,
  Rocket,
  Gem,
  Check,
  Shield,
  Zap,
  ChevronLeft,
  ChevronRight,
} from 'lucide-react';

import { useAuth } from '../../../../../contexts/auth/hooks';
import { useRegistration } from '../../../../../contexts/registration/hooks';
import { supabase } from '../../../../../lib/supabase';
import toast from 'react-hot-toast';
import { createCheckoutSession } from '../../../../../lib/stripeplanselection';

/** ===== Types ===== */
interface Plan {
  id: string;
  plan_key: string;
  code: string;
  name: string;
  description: string;
  monthly_price: number;
  yearly_price: number;
  stripe_price_id_monthly: string;
  stripe_price_id_yearly: string;
  features: any;
  limits: any;
  ai_tokens_limit: number | null;
  ai_display_limit: number | null;
  is_active: boolean;
}

/** ===== UI maps ===== */
const planIcons: Record<string, React.ReactNode> = {
  basic: <Star className="w-6 h-6" />,
  medium: <Rocket className="w-6 h-6" />,
  vip: <Gem className="w-6 h-6" />,
};

const planColors: Record<string, { from: string; to: string; accent: string; text: string; border: string; shadow: string }> = {
  basic: {
    from: 'from-blue-50',
    to: 'to-blue-100',
    accent: 'bg-blue-600',
    text: 'text-blue-600',
    border: 'border-blue-500',
    shadow: 'rgba(59, 130, 246, 0.25)'
  },
  medium:{
    from: 'from-purple-50',
    to: 'to-purple-100',
    accent: 'bg-purple-600',
    text: 'text-purple-600',
    border: 'border-purple-500',
    shadow: 'rgba(168, 85, 247, 0.25)'
  },
  vip:   {
    from: 'from-amber-50',
    to: 'to-amber-100',
    accent: 'bg-gradient-to-r from-amber-500 to-orange-500',
    text: 'text-amber-600',
    border: 'border-amber-500',
    shadow: 'rgba(251, 146, 60, 0.25)'
  },
};

/** ===== utils ===== */
function useIsMobile(breakpoint = 768) {
  const [isMobile, setIsMobile] = React.useState(false);
  React.useEffect(() => {
    const update = () => setIsMobile(window.innerWidth < breakpoint);
    update();
    window.addEventListener('resize', update);
    window.addEventListener('orientationchange', update);
    return () => {
      window.removeEventListener('resize', update);
      window.removeEventListener('orientationchange', update);
    };
  }, [breakpoint]);
  return isMobile;
}

function debounce<T extends (...a: any[]) => void>(fn: T, ms = 120) {
  let t: any;
  return (...args: Parameters<T>) => {
    clearTimeout(t);
    t = setTimeout(() => fn(...args), ms);
  };
}

/** ===== Component ===== */
export default function PlanSelection({ onSelect }: { onSelect?: (planId: string) => void }) {
  const { user, business } = useAuth();
  const { state } = useRegistration();

  const [plans, setPlans] = useState<Plan[]>([]);
  const [loading, setLoading] = useState(true);
  const [isYearly, setIsYearly] = useState(false);
  const [centeredKey, setCenteredKey] = useState<string>('medium');
  const [showLoading, setShowLoading] = useState(false);
  const [isRedirecting, setIsRedirecting] = useState(false);

  const isMobile = useIsMobile(768);

  const carouselRef = React.useRef<HTMLDivElement>(null);

  // keep refs for each card
  const cardRefs = React.useRef<HTMLDivElement[]>([]);
  cardRefs.current = [];

  /** ---- Load plans ---- */
  useEffect(() => {
    (async () => {
      try {
        setLoading(true);
        const { data, error } = await supabase
          .from('subscription_plans')
          .select('*')
          .eq('is_active', true)
          .order('monthly_price', { ascending: true });

        if (error) throw error;
        setPlans((data as Plan[]) || []);
      } catch (e) {

        toast.error('שגיאה בטעינת חבילות');
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  /** ---- Center initial ---- */
  useEffect(() => {
    if (!plans.length) return;
    const mediumIdx = plans.findIndex(p => p.plan_key === 'medium');
    const idx = mediumIdx !== -1 ? mediumIdx : 0;
    setTimeout(() => scrollToIndex(idx, false), 100);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [plans]);

  /** ---- Price / Features ---- */
  const formatPrice = (price: number) =>
    new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 0 }).format(price);

  const getFeaturesList = (plan: Plan): Array<{ text: string; included: boolean }> => {
    const features: Array<{ text: string; included: boolean }> = [];
    const limits = plan.limits || {};
    const maxCustomers = limits.max_customers;
    const maxWhatsapp = limits.max_whatsapp_messages;

    if (maxCustomers !== undefined) {
      features.push({
        text: maxCustomers === 0 || maxCustomers === -1 ? 'ללא הגבלת תורים' : `עד ${maxCustomers} לקוחות`,
        included: true,
      });
    }
    features.push({ text: 'גישה מכל מקום – מחשב ואפליקציה', included: true });
    features.push({ text: 'סטטיסטיקה ודוחות מתקדמים', included: true });

    if (plan.ai_display_limit !== null && plan.ai_display_limit > 0) {
      features.push({ text: `עד ${plan.ai_display_limit.toLocaleString('he-IL')} פעולות AI`, included: true });
    } else {
      features.push({ text: 'פעולות AI', included: false });
    }

    if (maxWhatsapp !== undefined && maxWhatsapp !== null) {
      if (maxWhatsapp === -1)      features.push({ text: 'WhatsApp ללא הגבלה', included: true });
      else if (maxWhatsapp === 0)  features.push({ text: 'הודעות WhatsApp', included: false });
      else                         features.push({ text: `עד ${maxWhatsapp.toLocaleString('he-IL')} הודעות WhatsApp`, included: true });
    }

    const isBasic = plan.plan_key === 'basic' || plan.code === 'basic';
    features.push({ text: 'תוכנית נאמנות', included: !isBasic });
    features.push({ text: 'חשבוניות וקופה', included: !isBasic });
    return features;
  };

  const isPopular = useMemo(() => (key: string) => key === 'medium', []);

  /** ---- Geometry helpers ---- */
  const getCenteredIndex = React.useCallback(() => {
    const container = carouselRef.current;
    if (!container) return -1;
    const containerRect = container.getBoundingClientRect();
    const cx = containerRect.left + containerRect.width / 2;

    let min = Infinity;
    let best = -1;
    cardRefs.current.forEach((el, i) => {
      if (!el) return;
      const r = el.getBoundingClientRect();
      const cardCenter = r.left + r.width / 2;
      const d = Math.abs(cardCenter - cx);
      if (d < min) {
        min = d;
        best = i;
      }
    });
    return best;
  }, []);

  const scrollToIndex = React.useCallback(
    (idx: number, smooth = true) => {
      const container = carouselRef.current;
      const card = cardRefs.current[idx];
      if (!container || !card) return;

      const containerRect = container.getBoundingClientRect();
      const targetRect = card.getBoundingClientRect();
      const delta = (targetRect.left + targetRect.width / 2) - (containerRect.left + containerRect.width / 2);

      container.scrollTo({
        left: container.scrollLeft + delta,
        behavior: smooth ? 'smooth' : 'auto',
      });
    },
    []
  );

  /** ---- Scroll listener ---- */
  useEffect(() => {
    const container = carouselRef.current;
    if (!container) return;

    const updateCentered = () => {
      const idx = getCenteredIndex();
      if (idx >= 0 && plans[idx]) {
        const key = plans[idx].plan_key;
        if (key !== centeredKey) {
          setCenteredKey(key);
        }
      }
    };

    const onScroll = debounce(() => {
      updateCentered();
    }, 80);

    const onScrollEnd = debounce(() => {
      updateCentered();
    }, 150);

    container.addEventListener('scroll', onScroll, { passive: true });
    container.addEventListener('scrollend', onScrollEnd, { passive: true });

    const ro = new ResizeObserver(() => updateCentered());
    ro.observe(container);

    updateCentered();

    return () => {
      container.removeEventListener('scroll', onScroll as any);
      container.removeEventListener('scrollend', onScrollEnd as any);
      ro.disconnect();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [plans, getCenteredIndex, centeredKey]);

  /** ---- Actions ---- */
  const handleCardClick = (key: string) => {
    const index = plans.findIndex(p => p.plan_key === key);
    if (index !== -1) scrollToIndex(index, true);
  };

  const handleCheckout = async (planKey: string) => {
    if (!plans.length) return;
    setIsRedirecting(true);
    setShowLoading(true);

    try {
      const businessId = localStorage.getItem('businessId');
      if (!businessId) {
        toast.error('לא נמצא מזהה עסק. חזור לשלב 4 והשלם את ההרשמה.');
        setIsRedirecting(false); setShowLoading(false); return;
      }

      const plan = plans.find(p => p.plan_key === planKey);
      if (!plan) {
        toast.error('חבילה לא נמצאה');
        setIsRedirecting(false); setShowLoading(false); return;
      }

      const period = isYearly ? 'yearly' : 'monthly';
      const priceId = isYearly ? plan.stripe_price_id_yearly : plan.stripe_price_id_monthly;
      const customerEmail = state?.stepsData?.step1?.email || user?.email || undefined;

      localStorage.setItem('selectedPlan', planKey);
      localStorage.setItem('selectedPlanCycle', period);
      localStorage.setItem('completed', '1');

      const metadata: Record<string, string> = {
        plan_key: planKey,
        billing_cycle: period,
        business_id: businessId,
        source: 'onboarding_plan_selection',
      };
      const businessName = state?.stepsData?.step2?.name || state?.stepsData?.step2?.businessName || business?.name;
      if (businessName) metadata.businessName = businessName;
      if (user?.id) metadata.user_id = user.id;

      const url = await createCheckoutSession(priceId, 'subscription', customerEmail, metadata);
      if (!url) {
        throw new Error('Checkout URL לא חזר מהשרת');
      }

      await new Promise(r => setTimeout(r, 600));
      window.location.href = url;
    } catch (err: any) {

      toast.error(err?.message || 'שגיאה בתהליך התשלום');
      setShowLoading(false);
      setIsRedirecting(false);
    }
  };

  /** ---- Redirect overlay ---- */
  if (showLoading || isRedirecting) {
    return (
      <div className="fixed inset-0 bg-white z-50 flex items-center justify-center">
        <div className="max-w-md w-full mx-4 text-center">
          <motion.div initial={{ scale: 0.8, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} transition={{ duration: 0.3 }} className="space-y-6">
            <div className="relative mx-auto w-24 h-24">
              <motion.div className="absolute inset-0 bg-gradient-to-r from-blue-500 to-purple-600 rounded-full" animate={{ rotate: 360 }} transition={{ duration: 1.5, repeat: Infinity, ease: 'linear' }} />
              <div className="absolute inset-2 bg-white rounded-full flex items-center justify-center">
                <Shield className="w-10 h-10 text-blue-600" />
              </div>
            </div>
            <div className="space-y-2">
              <h2 className="text-2xl font-bold text-gray-900">מעביר אותך לתשלום מאובטח</h2>
              <p className="text-gray-600">אנא המתן, אנחנו מכינים את עמוד התשלום…</p>
            </div>
            <div className="flex items-center justify-center gap-2">
              <motion.div className="w-2 h-2 bg-blue-600 rounded-full" animate={{ scale: [1, 1.5, 1], opacity: [1, 0.5, 1] }} transition={{ duration: 1, repeat: Infinity, ease: 'easeInOut' }} />
              <motion.div className="w-2 h-2 bg-purple-600 rounded-full" animate={{ scale: [1, 1.5, 1], opacity: [1, 0.5, 1] }} transition={{ duration: 1, repeat: Infinity, ease: 'easeInOut', delay: 0.2 }} />
              <motion.div className="w-2 h-2 bg-blue-600 rounded-full" animate={{ scale: [1, 1.5, 1], opacity: [1, 0.5, 1] }} transition={{ duration: 1, repeat: Infinity, ease: 'easeInOut', delay: 0.4 }} />
            </div>
            <div className="bg-blue-50 rounded-lg p-4">
              <p className="text-sm text-blue-800">לאחר השלמת התשלום תועבר/י אוטומטית למערכת LoyalFlow</p>
            </div>
          </motion.div>
        </div>
      </div>
    );
  }

  /** ---- Loading skeleton ---- */
  if (loading) {
    return (
      <div dir="rtl" className="w-full">
        <div className="bg-white px-6 py-4 flex items-center justify-between">
          <div className="h-8 w-32 bg-gray-200 rounded-lg animate-pulse" />
          <div className="flex items-center gap-2">
            <div className="h-8 w-16 bg-gray-200 rounded-lg animate-pulse" />
            <div className="h-8 w-16 bg-gray-200 rounded-lg animate-pulse" />
          </div>
        </div>
        <div className="relative py-8">
          <div className="flex items-center gap-6 overflow-x-hidden">
            <div className="flex-shrink-0" style={{ width: 'calc(50vw - 140px)' }} />
            {[0,1,2].map(i => (
              <div key={i} className="flex-shrink-0 w-[280px] min-h-[550px] rounded-[24px] border border-gray-100 bg-white p-6 flex flex-col">
                <div className="h-12 w-12 bg-gray-100 rounded-2xl mx-auto mb-4 animate-pulse" />
                <div className="h-6 w-24 bg-gray-100 rounded mx-auto mb-6 animate-pulse" />
                <div className="h-10 w-32 bg-gray-100 rounded mx-auto mb-6 animate-pulse" />
                <div className="space-y-3 mb-6 flex-1">
                  {[0,1,2,3,4,5,6].map(j => <div key={j} className="h-4 w-full bg-gray-100 rounded animate-pulse" />)}
                </div>
                <div className="h-12 w-full bg-gray-100 rounded-2xl mt-auto animate-pulse" />
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  /** ---- View ---- */
  return (
    <div dir="rtl" className="w-full overflow-hidden">
      {/* Header */}
      <div className="bg-white px-6 py-4 flex items-center justify-between">
        <h2 className="text-2xl font-bold text-gray-900">בחירת מסלול</h2>
        <div className="flex items-center gap-2">
          <button onClick={() => setIsYearly(false)} className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${!isYearly ? 'bg-blue-600 text-white' : 'bg-gray-100 text-gray-600'}`}>חודשי</button>
          <button onClick={() => setIsYearly(true)}  className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${ isYearly ? 'bg-blue-600 text-white' : 'bg-gray-100 text-gray-600'}`}>שנתי</button>
        </div>
      </div>

      {/* Carousel */}
      <div className="relative py-8 min-h-[580px] max-h-[85vh] overflow-hidden">
        {/* Frame + Arrows */}
        <div className="absolute inset-0 flex items-center justify-center pointer-events-none z-10">
          <div className="relative w-[280px] h-full max-h-[550px]">
            <div
              className={`w-full h-full rounded-[28px] border-[2px] transition-all duration-500 ease-out ${
                planColors[centeredKey]?.border || 'border-blue-500'
              }`}
              style={{
                boxShadow: `0 0 0 1px rgba(255,255,255,0.1), 0 8px 32px ${planColors[centeredKey]?.shadow || 'rgba(59,130,246,0.25)'}`,
              }}
            />
            {/* Arrows (mobile only) */}
            {plans.length > 1 && isMobile && (() => {
              const currentIndex = plans.findIndex(p => p.plan_key === centeredKey);
              if (currentIndex === -1) return null;
              const canGoPrev = currentIndex > 0;
              const canGoNext = currentIndex < plans.length - 1;

              const stopPointer = (e: React.PointerEvent) => { e.preventDefault(); e.stopPropagation(); };

              return (
                <>
                  {canGoPrev && (
                    <motion.button
                      key="right-arrow"
                      initial={{ opacity: 0, x: -10 }}
                      animate={{ opacity: 1, x: 0 }}
                      exit={{ opacity: 0, x: -10 }}
                      onPointerDown={stopPointer}
                      onClick={() => scrollToIndex(currentIndex - 1, true)}
                      className="absolute right-[-44px] top-1/2 -translate-y-1/2 pointer-events-auto z-30 focus:outline-none"
                      style={{ touchAction: 'pan-y' }}
                    >
                      <div className="w-12 h-12 rounded-full bg-white shadow-lg flex items-center justify-center border border-gray-200 hover:border-blue-400 active:scale-95">
                        <ChevronRight className="w-6 h-6 text-blue-600" />
                      </div>
                    </motion.button>
                  )}
                  {canGoNext && (
                    <motion.button
                      key="left-arrow"
                      initial={{ opacity: 0, x: 10 }}
                      animate={{ opacity: 1, x: 0 }}
                      exit={{ opacity: 0, x: 10 }}
                      onPointerDown={stopPointer}
                      onClick={() => scrollToIndex(currentIndex + 1, true)}
                      className="absolute left-[-44px] top-1/2 -translate-y-1/2 pointer-events-auto z-30 focus:outline-none"
                      style={{ touchAction: 'pan-y' }}
                    >
                      <div className="w-12 h-12 rounded-full bg-white shadow-lg flex items-center justify-center border border-gray-200 hover:border-blue-400 active:scale-95">
                        <ChevronLeft className="w-6 h-6 text-blue-600" />
                      </div>
                    </motion.button>
                  )}
                </>
              );
            })()}
          </div>
        </div>

        {/* Side fades */}
        <div className="absolute left-0 top-0 bottom-0 w-20 bg-gradient-to-r from-gray-50 to-transparent pointer-events-none z-[5]" />
        <div className="absolute right-0 top-0 bottom-0 w-20 bg-gradient-to-l from-gray-50 to-transparent pointer-events-none z-[5]" />

        {/* Scroll area */}
        <div
          ref={carouselRef}
          dir="ltr"
          className="flex items-center gap-6 overflow-x-auto overflow-y-hidden snap-x snap-mandatory scrollbar-hide h-full"
          style={{
            scrollSnapType: 'x mandatory',
            scrollPaddingInline: 'calc(50vw - 140px)',
            WebkitOverflowScrolling: 'touch',
            overscrollBehaviorX: 'contain',
            overscrollBehaviorY: 'none',
            scrollSnapStop: 'always',
            touchAction: 'pan-x',
          }}
        >
          <div className="flex-shrink-0" style={{ width: 'calc(50vw - 140px)' }} />
          {plans.map((plan, index) => {
            const colors = planColors[plan.plan_key] || planColors.basic;
            const monthlyPrice = plan.monthly_price;
            const yearlyPrice = plan.yearly_price;
            const displayPrice = isYearly ? (yearlyPrice / 12) : monthlyPrice;
            const yearlySavings = monthlyPrice * 12 - yearlyPrice;
            const savingsPercent = Math.round((yearlySavings / (monthlyPrice * 12)) * 100);
            const features = getFeaturesList(plan);
            const isCentered = centeredKey === plan.plan_key;

            return (
              <div
                key={plan.id}
                ref={el => { if (el) cardRefs.current[index] = el; }}
                className={`relative flex-shrink-0 w-[280px] min-h-[520px] max-h-[550px] rounded-[24px] bg-white border-2 transition-all duration-500 ease-out snap-center overflow-y-auto ${
                  isCentered ? `${colors.border}` : 'border-gray-200/40 opacity-90'
                }`}
                style={{
                  scrollSnapAlign: 'center',
                  transform: 'scale(1)',
                  boxShadow: isCentered
                    ? `0 20px 50px ${colors.shadow}, 0 8px 20px rgba(0,0,0,0.06)`
                    : '0 4px 8px rgba(0,0,0,0.03)',
                }}
                onClick={() => handleCardClick(plan.plan_key)}
              >
                <div className="p-6 pt-8 pb-6 h-full flex flex-col">
                  {/* header */}
                  <div className="text-center mb-4">
                    <div className={`inline-flex p-3 rounded-2xl ${colors.accent} text-white mb-3`}>
                      {planIcons[plan.plan_key] || <Star className="w-7 h-7" />}
                    </div>
                    <h3 className="text-xl font-bold text-gray-900 mb-1">{plan.name}</h3>
                    {isPopular(plan.plan_key) && (
                      <div className="inline-flex items-center gap-1 px-3 py-1 bg-blue-50 text-blue-600 rounded-full text-xs font-medium">
                        <Zap className="w-3 h-3" />
                        מומלץ ביותר
                      </div>
                    )}
                  </div>

                  {/* price */}
                  <div className="text-center mb-4">
                    <div className="flex items-baseline justify-center gap-1">
                      <span className="text-3xl font-bold text-gray-900">{formatPrice(displayPrice)}</span>
                      <span className="text-gray-500 text-xs">/ חודש</span>
                    </div>
                    {isYearly && savingsPercent > 0 && (
                      <p className="text-xs font-medium text-green-600 mt-1.5">חיסכון של {savingsPercent}% בתשלום שנתי</p>
                    )}
                  </div>

                  {/* features */}
                  <ul className="space-y-2.5 mb-5 flex-1">
                    {features.map((feature, idx) => (
                      <li key={idx} className="flex items-center gap-2.5">
                        {feature.included ? (
                          <div className="w-5 h-5 rounded-full bg-green-50 flex items-center justify-center flex-shrink-0">
                            <Check className="w-3 h-3 text-green-600" />
                          </div>
                        ) : (
                          <div className="w-5 h-5 rounded-full border-2 border-gray-200 flex-shrink-0" />
                        )}
                        <span className={`${feature.included ? 'text-gray-700' : 'text-gray-400'} text-sm leading-tight`}>{feature.text}</span>
                      </li>
                    ))}
                  </ul>

                  {/* CTA */}
                  <motion.button
                    onClick={(e) => {
                      e.stopPropagation();
                      if (isCentered && !isRedirecting && !showLoading) {
                        onSelect?.(plan.plan_key);
                        handleCheckout(plan.plan_key);
                      }
                    }}
                    disabled={!isCentered || isRedirecting || showLoading}
                    animate={
                      isCentered && !isRedirecting && !showLoading
                        ? {
                            boxShadow: [
                              '0 4px 14px rgba(0, 0, 0, 0.1)',
                              '0 6px 20px rgba(0, 0, 0, 0.15)',
                              '0 4px 14px rgba(0, 0, 0, 0.1)',
                            ],
                          }
                        : {}
                    }
                    transition={{
                      duration: 2,
                      repeat: Infinity,
                      ease: 'easeInOut',
                    }}
                    className={`w-full py-3.5 px-6 rounded-2xl font-semibold text-base transition-all ${
                      !isCentered
                        ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                        : isRedirecting || showLoading
                        ? 'bg-gray-300 text-gray-500 cursor-wait'
                        : `${colors.accent} text-white hover:opacity-90 active:scale-95`
                    }`}
                  >
                    {!isCentered ? 'גלול לבחירה' : isRedirecting || showLoading ? 'מעביר לתשלום...' : 'בחר חבילה'}
                  </motion.button>
                </div>
              </div>
            );
          })}
          <div className="flex-shrink-0" style={{ width: 'calc(50vw - 140px)' }} />
        </div>
      </div>

      {/* trust */}
      <div className="text-center mt-4 pb-4">
        <p className="text-xs text-gray-500">14 ימי ניסיון חינם • ביטול בכל עת</p>
      </div>
    </div>
  );
}
